import { useState } from "react";
import { toast } from "sonner";
import { useSWRConfig } from "swr";
import { CreateCommandRequest, UpdateCommandRequest } from "@/services/agent/agent.api.types";
import { AgentService } from "@/services/agent/agent.service";

export const useCommandMutations = () => {
  const { mutate } = useSWRConfig();
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const invalidateCommandCaches = (commandId?: string) => {
    // コマンド一覧のキャッシュを無効化（全てのworkspaceIdとenabledOnlyの組み合わせ）
    mutate(
      (key) => Array.isArray(key) && key[0] === "agent" && key[1] === "commands",
      undefined,
      { revalidate: true },
    );

    // 個別のコマンドキャッシュを無効化（commandIdが指定されている場合）
    if (commandId) {
      mutate(["agent", "command", commandId], undefined, { revalidate: true });
    }
  };

  const createCommand = async (data: CreateCommandRequest) => {
    setIsCreating(true);
    try {
      const result = await AgentService.createCommand(data);
      toast.success("コマンドを作成しました");
      invalidateCommandCaches();
      return result;
    } catch (error) {
      console.error("Failed to create command:", error);
      toast.error("コマンドの作成に失敗しました");
      throw error;
    } finally {
      setIsCreating(false);
    }
  };

  const updateCommand = async (commandId: string, data: UpdateCommandRequest) => {
    setIsUpdating(true);
    try {
      const result = await AgentService.updateCommand(commandId, data);
      toast.success("コマンドを更新しました");
      invalidateCommandCaches(commandId);
      return result;
    } catch (error) {
      console.error("Failed to update command:", error);
      toast.error("コマンドの更新に失敗しました");
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  const deleteCommand = async (commandId: string, commandName: string) => {
    if (!confirm(`コマンド「${commandName}」を削除してもよろしいですか？`)) {
      return;
    }

    setIsDeleting(true);
    try {
      await AgentService.deleteCommand(commandId);
      toast.success("コマンドを削除しました");
      invalidateCommandCaches(commandId);
    } catch (error) {
      console.error("Failed to delete command:", error);
      toast.error("コマンドの削除に失敗しました");
      throw error;
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    createCommand,
    updateCommand,
    deleteCommand,
    isCreating,
    isUpdating,
    isDeleting,
  };
};
